import { useRef, useEffect, useState } from "react";
import styles from "../ResourcesLayout.module.css";

/**
 * WorkflowDiagram — Renders a horizontal step-by-step workflow.
 *
 * Props:
 *   steps: string[]    — Workflow step labels
 *   label?: string     — Optional accessible label for the diagram
 *
 * Desktop: horizontal row with arrow connectors.
 * Mobile: wraps naturally or scrolls horizontally.
 */
const WorkflowDiagram = ({ steps, label }) => {
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const check = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth + 4);
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [steps]);

  if (!steps || steps.length === 0) return null;

  return (
    <div className={styles["workflow-wrapper"]} role="img" aria-label={label || "Workflow diagram"}>
      <div
        ref={containerRef}
        className={`${styles["workflow-container"]} ${isOverflowing ? styles["workflow-scrollable"] : ""}`}
      >
        {steps.map((step, idx) => (
          <div key={idx} className={styles["workflow-item"]}>
            <div className={styles["workflow-step"]}>
              <span className={styles["workflow-step-number"]}>{idx + 1}</span>
              <span className={styles["workflow-step-label"]}>{step}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={styles["workflow-arrow"]} aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12M12 6l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
            )}
          </div>
        ))}
      </div>
      {isOverflowing && (
        <div className={styles["workflow-scroll-hint"]} aria-hidden="true">
          Scroll →
        </div>
      )}
    </div>
  );
};

export default WorkflowDiagram;
